/**
 * # FileInput
 *
 * A component that allows users to select and upload files.
 *
 * ## Props extends InputHTMLAttributes<HTMLInputElement>
 *
 * - `accept` (string): The file types to accept, specified as a comma-separated list of MIME types or file extensions.
 * - `className` (string): Additional CSS class name for the component.
 * - `disabled` (boolean): Determines whether the component is disabled or not. Default is `false`.
 * - `error` (boolean): Determines whether the component is in an error state or not. Default is `false`.
 * - `warning` (boolean): Determines whether the component is in a warning state or not. Default is `false`.
 * - `errorMessage` (React.ReactNode): The error message to display when the component is in an error state.
 * - `icon` (IconProp): The icon to display in the component. Default is `'upload'`.
 * - `iconStyle` (IconStyle): The style of the icon. Default is `'light'`.
 * - `multiple` (boolean): Determines whether multiple files can be selected or not. Default is `false`.
 * - `onChange` ((e?: React.ChangeEvent<HTMLInputElement>) => void): The callback function to be called when the value of the input changes.
 * - `placeholder` (string): The placeholder text to display when no file is chosen. Default is `'No file chosen'`.
 * - `...rest: InputHTMLAttributes<HTMLInputElement>`: See [MDN reference](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input) for `input`
 *
 * ## Usage
 *
 * ```tsx
 * import FileInput from './FileInput.react';
 *
 * const MyComponent = () => {
 *   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 *     // Handle file change event
 *   };
 *
 *   return (
 *     <FileInput
 *       accept=".jpg,.png"
 *       className="my-file-input"
 *       disabled={false}
 *       error={false}
 *       warning={false}
 *       errorMessage="Invalid file format"
 *       icon="upload"
 *       iconStyle="light"
 *       multiple={false}
 *       onChange={handleFileChange}
 *       placeholder="Select a file"
 *     />
 *   );
 * };
 *
 * export default MyComponent;
 * ```
 */
import React, {useCallback, useState, InputHTMLAttributes} from 'react';
import classnames from 'classnames';

import {arrayToReadableString} from 'lib/stringUtils';

import Icon, {IconProp, IconStyle} from '../../atoms/Icon/Icon.react';
import Text from '../../atoms/Text/Text.react';
import './file-input.scss';

// TODO: Add a drag and drop zone

interface FileInputProps
  extends
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'onChange' | 'placeholder' | 'multiple' | 'disabled' | 'accept'
    >,
    PropsWithClassNameOptional {
  accept: string;
  disabled?: boolean;
  error?: boolean;
  warning?: boolean;
  errorMessage?: React.ReactNode;
  icon?: IconProp;
  iconStyle?: IconStyle;
  multiple?: boolean;
  onChange?: (e?: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

function hasFiles(files: FileList | null): files is FileList {
  return files !== null;
}

const FileInput = ({
  accept,
  className,
  disabled = false,
  error = false,
  warning = false,
  errorMessage,
  icon = 'upload',
  iconStyle = 'light',
  multiple = false,
  placeholder = 'No file chosen',
  onChange = () => {},
  ...rest
}: FileInputProps) => {
  const [filenames, setFilenames] = useState<string | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist();
      const {files} = e.target;
      if (hasFiles(files)) {
        const filenamesArr = Array.from(files).map((file) => file.name);
        const newFilenames = arrayToReadableString(filenamesArr);
        setFilenames(newFilenames);
        onChange(e);
      }
    },
    [onChange]
  );

  return (
    <label
      className={classnames(
        className,
        'file-input flex grow flex-col flex-wrap items-center justify-center px-x3 py-x3 text-center',
        {
          'border-info background-info-subtle content-info hover:background-info-subtle-hover':
            !warning && !error && !filenames,
          'border-primary background-subtle content-secondary hover:background-subtle-hover':
            !warning && !error && filenames,
          // error state takes precedence over warning
          'file-input--warning border-warning background-warning-subtle content-warning':
            warning && !error && filenames,
          'file-input--error border-negative background-negative-subtle content-negative hover:background-negative-subtle-hover':
            error,
          'pointer-events-none border-primary background-subtle content-tertiary': disabled
        }
      )}
    >
      <Icon
        className={classnames(className, 'file-input__icon mb-x2', {
          'content-info': !error && !filenames,
          'content-tertiary-inverse': !error && filenames,
          'content-negative': error,
          'content-warning': warning,
          'content-secondary-inverse': disabled
        })}
        color='inherit'
        size='inherit'
        icon={!filenames ? icon : 'file-alt'}
        iconStyle={iconStyle}
      />
      <Text className='file-input__content grow' color='inherit' id='content'>
        {errorMessage || filenames || placeholder}
      </Text>
      {!disabled && (
        <Text className='mt-x1 underline' color='inherit'>
          {!filenames ? 'Choose...' : 'Change'}
        </Text>
      )}
      <input
        accept={accept}
        aria-describedby='content'
        className='file-input__input cursor-pointer'
        disabled={disabled}
        multiple={multiple}
        onChange={handleChange}
        type='file'
        {...(rest as any)}
      />
    </label>
  );
};

export default FileInput;
